import type { LucideIcon } from "lucide-react";
import {
  BarChart3Icon,
  Building2Icon,
  ChevronDown,
  HomeIcon,
  MessageCircle,
  Package,
  User,
  Users,
  X,
} from "lucide-react";
import React from "react";
import { Link, useLocation } from "react-router-dom";

import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  PermissionGuard,
} from "@/components";
import { config } from "@/config/config";
import { useAuth } from "@/context/auth";
import { useI18n } from "@/context/i18n";
import { usePermissions } from "@/context/permissions";
import { useSidebar } from "@/context/sidebar";
import { cn } from "@/lib/utils";

type TSidebarChild = {
  name: string;
  href: string;
  /** Se true, só ativa no path exato (ex.: /clients sem /clients/...) */
  exact?: boolean;
};

type TSidebarLinkEntry = {
  kind: "link";
  name: string;
  href: string;
  icon: LucideIcon;
  permission: string;
};

type TSidebarGroupEntry = {
  kind: "group";
  name: string;
  icon: LucideIcon;
  permission: string;
  children: TSidebarChild[];
};

type TSidebarEntry = TSidebarLinkEntry | TSidebarGroupEntry;

const sidebarEntries: TSidebarEntry[] = [
  {
    kind: "link",
    name: "dashboard.title",
    href: "/dashboard",
    icon: HomeIcon,
    permission: "dashboard:view",
  },
  {
    kind: "link",
    name: "dashboard.reports",
    href: "/reports",
    icon: BarChart3Icon,
    permission: "reports:view",
  },
  {
    kind: "link",
    name: "dashboard.insuranceCompanies",
    href: "/insurance-companies",
    icon: Building2Icon,
    permission: "insurance:view",
  },
  {
    kind: "link",
    name: "dashboard.products",
    href: "/products",
    icon: Package,
    permission: "products:view",
  },
  {
    kind: "group",
    name: "dashboard.clients",
    icon: Users,
    permission: "partners:view",
    children: [
      { name: "dashboard.clients.sub.list", href: "/clients", exact: true },
      {
        name: "dashboard.clients.sub.linkUsers",
        href: "/clients/select/link-users",
      },
      {
        name: "dashboard.clients.sub.projects",
        href: "/clients/select/projects",
      },
      {
        name: "dashboard.clients.sub.campaigns",
        href: "/clients/select/campaigns",
      },
    ],
  },
  {
    kind: "link",
    name: "dashboard.whatsappMeta",
    href: "/whatsapp-meta",
    icon: MessageCircle,
    permission: "whatsapp:view",
  },
  {
    kind: "link",
    name: "dashboard.users",
    href: "/users",
    icon: User,
    permission: "user:view",
  },
];

function isLinkActive(
  pathname: string,
  href: string,
  exact?: boolean
): boolean {
  if (exact) {
    return pathname === href || pathname === `${href}/`;
  }
  return pathname === href || pathname.startsWith(`${href}/`);
}

function isPartnersSectionPath(pathname: string): boolean {
  return pathname === "/clients" || pathname.startsWith("/clients/");
}

interface ISidebarNavItemsProps {
  onNavigate?: () => void;
  linkClassName: string;
}

function SidebarNavItems({
  onNavigate,
  linkClassName,
}: ISidebarNavItemsProps) {
  const location = useLocation();
  const { t } = useI18n();
  const { auth } = useAuth();
  const { hasPermission } = usePermissions();
  const isScopedPartner =
    hasPermission("partner:self:view") && !hasPermission("partners:view");

  const partnersPath = isPartnersSectionPath(location.pathname);
  const [partnersOpen, setPartnersOpen] = React.useState(partnersPath);
  const prevPathRef = React.useRef(location.pathname);

  React.useEffect(() => {
    const now = isPartnersSectionPath(location.pathname);
    const was = isPartnersSectionPath(prevPathRef.current);
    if (now && !was) {
      setPartnersOpen(true);
    }
    prevPathRef.current = location.pathname;
  }, [location.pathname]);

  return (
    <>
      {sidebarEntries.map((entry) => {
        if (entry.kind === "link") {
          return (
            <PermissionGuard key={entry.href} permission={entry.permission}>
              <Link
                to={entry.href}
                className={cn(
                  linkClassName,
                  isLinkActive(location.pathname, entry.href)
                    ? "bg-menu-item/5 text-menu-item"
                    : "hover:bg-muted"
                )}
                onClick={onNavigate}
              >
                <entry.icon className="h-5 w-5 shrink-0" />
                <span>{t(entry.name)}</span>
              </Link>
            </PermissionGuard>
          );
        }

        const groupActive = partnersPath;

        if (entry.name === "dashboard.clients" && isScopedPartner) {
          return (
            <PermissionGuard
              key={`${entry.name}-scoped`}
              permissions={["partner:self:view"]}
              requireAll={false}
            >
              <Link
                to="/clients"
                state={
                  auth.organization_root_id
                    ? { openClientId: auth.organization_root_id }
                    : undefined
                }
                className={cn(
                  linkClassName,
                  groupActive ? "bg-menu-item/5 text-menu-item" : "hover:bg-muted"
                )}
                onClick={onNavigate}
              >
                <entry.icon className="h-5 w-5 shrink-0" />
                <span>{t("dashboard.clients.myPartner")}</span>
              </Link>
            </PermissionGuard>
          );
        }

        return (
          <PermissionGuard key={entry.name} permission={entry.permission}>
            <Collapsible open={partnersOpen} onOpenChange={setPartnersOpen}>
              <CollapsibleTrigger
                className={cn(
                  linkClassName,
                  "w-full justify-between",
                  groupActive ? "bg-menu-item/5 text-menu-item" : "hover:bg-muted"
                )}
              >
                <span className="flex items-center gap-3 min-w-0">
                  <entry.icon className="h-5 w-5 shrink-0" />
                  <span className="truncate text-left">{t(entry.name)}</span>
                </span>
                <ChevronDown
                  className={cn(
                    "h-4 w-4 shrink-0 transition-transform duration-200",
                    partnersOpen ? "rotate-180" : "rotate-0"
                  )}
                />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="mt-1 ml-2 pl-2 border-l border-border/60 flex flex-col gap-0.5">
                  {entry.children.map((child) => (
                    <Link
                      key={child.href}
                      to={child.href}
                      className={cn(
                        "flex items-center rounded-md px-3 py-1.5 text-sm transition-colors",
                        isLinkActive(
                          location.pathname,
                          child.href,
                          child.exact
                        )
                          ? "bg-menu-item/5 text-menu-item font-medium"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                      onClick={onNavigate}
                    >
                      {t(child.name)}
                    </Link>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </PermissionGuard>
        );
      })}
    </>
  );
}

export function Sidebar() {
  const { isOpen, close } = useSidebar();

  return (
    <>
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 bg-background border-r border-r-muted/55 transform transition-transform duration-200 ease-in-out md:hidden",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b border-b-muted/55">
          <Link to="/dashboard" className="flex items-center space-x-2">
            <img src={config.logoUrl} alt={config.brandName} className="w-12 h-12" />
            <span className="font-bold text-lg">{config.brandName}</span>
          </Link>
          <Button variant="ghost" size="icon" onClick={close}>
            <X className="h-5 w-5" />
          </Button>
        </div>
        <nav className="flex flex-col gap-1 p-2">
          <SidebarNavItems
            onNavigate={close}
            linkClassName="flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors"
          />
        </nav>
      </div>

      <div className="hidden md:flex md:w-50 md:flex-col md:h-full md:sticky md:top-0 md:self-start bg-background border-r border-r-muted/55">
        <div className="flex flex-col flex-1 min-h-0">
          <nav className="flex-1 overflow-y-auto p-2 min-h-0 flex flex-col gap-1">
            <SidebarNavItems linkClassName="flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors mb-0" />
          </nav>
        </div>
      </div>
    </>
  );
}
